// src/redux/slices/preShip/preShipApiSlice.ts
import { apiSlice } from "@/redux/apiSlice";

export interface LeanDetail {
  palletLpnNumberId: number;
  lpnNo: string;
  endStatusPallet: string;
  variant: string;
  sideABottom: string;
  sideATop: string;
  sideALean: string;
  sideBBottom: string;
  sideBTop: string;
  sideBLean: string;
  sideCBottom: string;
  sideCTop: string;
  sideCLean: string;
  sideDBottom: string;
  sideDTop: string;
  sideDLean: string;
}

export interface LeanDetailsResponse {
  message: string;
  leanDetails: LeanDetail[];
}
interface FTLRow {
  id?: number;
  palletLpnNumberId: number;
  lpnNo: string;
  endStatusPallet: string;
  variant: string;
  topMeasured: number;
  middleMeasured: number;
  bottomMeasured: number;
  notes: string;
}

interface GetFTLRowDetailsResponse {
  message: string;
  ftlRows: FTLRow[];
}

interface CreateFTLRowsRequest {
  type: 'create' | 'update';
  ftlRows: Omit<FTLRow, 'id'>[];
}

interface CreateFTLRowsResponse {
  message: string;
  ftlRows: FTLRow[];
}
interface UnitLoadPosition {
  id?: number;
  variant: string;
  position: string;
  height: number;
  width: number;
  depth: number;
  weight: number;
  notes: string;
}

interface PalletLpnNumber {
  id?: number;
  lpnNo: string;
  endStatusPallet: string;
  unitLoadPositions: UnitLoadPosition[];
}

interface GetUnitLoadPositionDetailsResponse {
  message: string;
  palletLpnNumbers: PalletLpnNumber[];
}

interface CreateUnitLoadPositionsRequest {
  palletId: number;
  palletLpnNumbers: PalletLpnNumber[];
}

interface CreateUnitLoadPositionsResponse {
  message: string;
  palletLpnNumbers: PalletLpnNumber[];
}
interface UnitLoadHeight {
  id?: number;
  variant: string;
  weekType: string;
  heightAB: number;
  heightBC: number;
  heightCD: number;
  heightDA: number;
}

interface PalletLpnNumber {
  id?: number;
  palletId?: number;
  lpnNo: string;
  endStatusPallet: string;
  unitLoadHeights: UnitLoadHeight[];
  createdAt?: string;
  updatedAt?: string;
}

interface GetUnitLoadHeightDetailsResponse {
  message: string;
  palletLpnNumbers: PalletLpnNumber[];
}

interface CreateUnitLoadHeightsRequest {
  palletId: number;
  palletLpnNumbers: PalletLpnNumber[];
}

interface CreateUnitLoadHeightsResponse {
  message: string;
  palletLpnNumbers: PalletLpnNumber[];
}
interface DefectKey {
  id: number;
  name: string;
}

interface RQLDefect {
  id?: number;
  defectKeyId: number;
  critical: number | null;
  major1: number | null;
  major2: number | null;
  minor: number | null;
}

interface RQL {
  id?: number;
  palletId: number;
  variant: 'preShip' | 'postShip' | 'stack';
  targetSampleSize: number | null;
  actualSampleSize: number | null;
  dateOfInspection: string | null;
  packageType: string | null;
  inspectedBy: string | null;
  totalDefects: number | null;
  percentDefects: number | null;
  passFail: string | null;
  unclassifiedDefects: string | null;
  rqlNotes: string | null;
  defects: RQLDefect[];
}

interface GetRQLDetailsResponse {
  message: string;
  rqls: RQL[];
  defectKeys: DefectKey[];
}

interface CreateOrUpdateRQLRequest {
  rqlData: Omit<RQL, 'defects'>;
  rqlDefects: RQLDefect[];
}

interface CreateOrUpdateRQLResponse {
  message: string;
  rql: RQL;
}
interface DefectKey {
  id: number;
  name: string;
}

interface GetAllDefectKeysResponse {
  message: string;
  defectKeys: DefectKey[];
}
export const preShipApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLeanDetails: builder.query({
        query: ({palletId,variant}:any) => `/projects/getLeanDetails/${palletId}/${variant}`,
      }),
      getPostStackLeanDetails: builder.query({
        query: ({palletId,variant,weekType}:any) => `/projects/getPostStackLeanDetails/${palletId}/${variant}/${weekType}`,
      }),
      createLeanDetails: builder.mutation({
        query: ({ palletId, palletLpnNumbers }) => ({
          url: `/projects/createLeanDetails`,
          method: 'POST',
          body: { palletLpnNumbers,palletId },
        }),
      }),
      getFTLRowDetails: builder.query({
        query: ({palletId,variant}:any) => `/projects/getFTLRowDetails/${palletId}/${variant}`,
      }),
      getPostStackLeanUniqueWeeks: builder.query({
        query: ({palletId,variant}:any) => `/projects/getPostStackLeanUniqueWeeks/${palletId}/${variant}`,
      }),
      getPostStackUnitLoadHeightUniqueWeeks: builder.query({
        query: ({palletId,variant}:any) => `/projects/getPostStackUnitLoadHeightUniqueWeeks/${palletId}/${variant}`,
      }),
      createFTLRows: builder.mutation({
        query: (body) => ({
          url: '/projects/createFTLRows',
          method: 'POST',
          body,
        }),
      }),
      getUnitLoadPositionDetails: builder.query({
        query: ({ palletId, variant }:any) => `/projects/getUnitLoadPositionDetails/${palletId}/${variant}`,
      }),
      createUnitLoadPositions: builder.mutation({
        query: (body) => ({
          url: '/projects/createUnitLoadPositions',
          method: 'POST',
          body,
        }),
      }),
      getUnitLoadHeightDetails: builder.query({
        query: ({ palletId, variant }:any) => `/projects/getUnitLoadHeightDetails/${palletId}/${variant}`,
      }),
      getPostStackUnitLoadHeightDetails: builder.query({
        query: ({ palletId, variant,weekType }:any) => `/projects/getPostStackUnitLoadHeightDetails/${palletId}/${variant}/${weekType}`,
      }),
      createUnitLoadHeights: builder.mutation({
        query: (body) => ({
          url: '/projects/createUnitLoadHeights',
          method: 'POST',
          body,
        }),
      }),
      getRQLDetails: builder.query({
        query: ({ palletId, variant }:any) => `/projects/getRQLDetails/${palletId}/${variant}`,
      }),
      getRQLsForPallet: builder.query({
        query: ( palletId) => `/projects/getRQLsForPallet/${palletId}`,
      }),
      createRQL: builder.mutation({
        query: (body) => ({
          url: '/projects/createRQL',
          method: 'POST',
          body,
        }),
      }),
      getAllDefectKeys: builder.query({
        query: () => '/projects/getAllDefectKeys',
        providesTags: ['DefectKeys'],
      }),
      getPostShipTruckTestLayoutDetails: builder.query({
        query: (shippingModeId) => `/projects/getPostShipTruckTestLayoutDetails/${shippingModeId}`,
      }),
      createPostShipTruckTestLayout: builder.mutation({
        query: (body) => ({
          url: '/projects/createPostShipTruckTestLayout',
          method: 'POST',
          body,
        }),
      }),
      createPostStackLayoutTest: builder.mutation({
        query: (body) => ({
          url: '/projects/createPostStackLayoutTest',
          method: 'POST',
          body,
        }),
      }),

      updatePalletsForTestStack: builder.mutation({
        query: (body) => ({
          url: '/projects/updatePalletsForTestStack',
          method: 'POST',
          body,
        }),
      }),

      getPostStackLayoutTestDetails: builder.query({
        query: (projectId) => `/projects/getPostStackLayoutTestDetails/${projectId}`,
      }),
      getPalletsForTestStack: builder.query({
        query: (projectId) => `/projects/getPalletsForTestStack/${projectId}`,
      }),
      getLeanConsolidatedData: builder.query({
        query: ({projectId,variant,weekType,isSum}:any) =>(variant==='postStack' && !isSum)?`/projects/getPostStackLeanConsolidatedData/${projectId}/${variant}/${weekType}`: `/projects/getLeanConsolidatedData/${projectId}/${variant}`,

      }),
      palletConfigValidation: builder.query({
        query: (projectId) => `/projects/validatePalletConfig/${Number(projectId)}`,
      }),
      getUnitLoadPositionConsolidatedData: builder.query({
        query: ({ projectId, variant }) => `/projects/getUnitLoadPositionConsolidatedData/${projectId}/${variant}`,
      }),
      getUnitLoadHeightConsolidatedData: builder.query({
        query: ({ projectId, variant,weekType,isSum }:any) => {
          console.log(weekType,'inside redux')
          if(variant==='postStack' && !isSum){
            return `/projects/getPostStackUnitLoadHeightConsolidatedData/${projectId}/${variant}/${weekType}`
          }else{
          return `/projects/getUnitLoadHeightConsolidatedData/${projectId}/${variant}`

          }
        },
      }),
  }),
});

export const {
  useGetLeanDetailsQuery,
  useGetLeanConsolidatedDataQuery,
  useGetPostStackLeanDetailsQuery,
  useCreateLeanDetailsMutation,
  useGetFTLRowDetailsQuery,
  useCreateFTLRowsMutation,
  useGetUnitLoadPositionDetailsQuery,
  useCreateUnitLoadPositionsMutation,
  useGetUnitLoadPositionConsolidatedDataQuery,
  useGetUnitLoadHeightDetailsQuery,
  useGetUnitLoadHeightConsolidatedDataQuery,
  useCreateUnitLoadHeightsMutation,
  useGetRQLDetailsQuery,
  useCreateRQLMutation,
  useGetAllDefectKeysQuery,
  useGetPostShipTruckTestLayoutDetailsQuery,
  useCreatePostShipTruckTestLayoutMutation,
  useGetPostStackUnitLoadHeightDetailsQuery,
  useGetPostStackLayoutTestDetailsQuery,
  useCreatePostStackLayoutTestMutation,
  useGetPostStackLeanUniqueWeeksQuery,
  useGetPostStackUnitLoadHeightUniqueWeeksQuery,
  useUpdatePalletsForTestStackMutation,
  useGetPalletsForTestStackQuery,
  useGetRQLsForPalletQuery,
  usePalletConfigValidationQuery
} = preShipApiSlice;
